/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import PageLoader from '../../components/page-loader'
import {Helmet} from 'react-helmet'
import {useIntl} from 'react-intl'

/* Hooks */
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'

/* Components */
import Optimizely from '../../components/optimizely'
import ContentFallback from '../../components/optimizely/utils/contentFallback'
import {useBrand} from '../../contexts/brand-context'

const PageNotFound = () => {
    const intl = useIntl()
    const {res} = useServerContext()
    const {isGarage} = useBrand()

    if (res) {
        res.status(404)
    }

    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('error-page')

    if (isOptimizelyLoading) {
        return (
            <Box data-testid="error-page">
                <PageLoader />
            </Box>
        )
    }

    /* When the active (brand, locale) tree has no 'error-page' content, show the
     * diagnostic banner instead of rendering an empty <Optimizely /> — which
     * renders <PageNotFound /> and would recurse infinitely. Mirrors the
     * homepage/content-page workaround. Guard on `.item` (the exact field
     * <Optimizely> renders on) so the empty case can never reach it. */
    const errorContent = optimizelyData?.page || optimizelyData
    const hasContent = !!errorContent?.item

    return (
        <Box data-testid="error-page">
            <Helmet>
                <title>
                    {isGarage
                        ? intl.formatMessage({
                              defaultMessage: "The page you're looking for can't be found.",
                              id: 'page_not_found.title.page_cant_be_found.garage'
                          })
                        : intl.formatMessage({
                              defaultMessage: "The page you're looking for can't be found.",
                              id: 'page_not_found.title.page_cant_be_found.dynamite'
                          })}
                </title>
            </Helmet>
            {hasContent ? (
                <Box>
                    <Optimizely
                        content={errorContent}
                        isLoading={isOptimizelyLoading}
                        contentKey="error-page"
                    />
                </Box>
            ) : (
                // showErrorPage=false: this IS the error page — don't recurse into it.
                <ContentFallback contentKey="error-page" showErrorPage={false} />
            )}
        </Box>
    )
}

export default PageNotFound
